import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import AuthedImage from "@/components/AuthedImage";
import { Trash2, Lock, FolderInput, MapPin, Calendar, UserCircle2, RotateCw, RotateCcw } from "lucide-react";

export default function Pending() {
  const [photos, setPhotos] = useState([]);
  const [albums, setAlbums] = useState([]);

  const reload = useCallback(async () => {
    const [p, a] = await Promise.all([
      api.get("/photos/pending"),
      api.get("/albums"),
    ]);
    setPhotos(p.data.photos);
    setAlbums(a.data.albums);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const decide = async (photo, action, albumId = null) => {
    if (action === "delete") {
      await api.delete(`/photos/${photo.id}`);
    } else if (action === "private") {
      await api.patch(`/photos/${photo.id}`, { status: "private" });
    } else if (action === "public") {
      await api.patch(`/photos/${photo.id}`, { status: "public", album_id: albumId });
    }
    reload();
  };

  const rotate = async (photoId, direction) => {
    try {
      await api.post(`/photos/${photoId}/rotate`, null, { params: { direction } });
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Rotation failed");
    }
  };

  return (
    <div className="paper-grain min-h-screen" data-testid="pending-page">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <p className="font-script text-2xl text-[#3A5A40]">a moment to review</p>
        <h1 className="font-serif-display text-4xl mb-2">Pending photos</h1>
        <p className="text-[#5C5C5C] mb-10">These were uploaded by friends. Choose what to do with each one.</p>

        {photos.length === 0 && (
          <div className="paper-card p-12 text-center">
            <p className="font-script text-3xl text-[#5C5C5C] mb-2">all caught up</p>
            <p className="text-[#8A8A8A]">No photos waiting for review.</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {photos.map((p) => (
            <div key={p.id} className="paper-card p-6" data-testid={`pending-photo-${p.id}`}>
              <div className="aspect-[4/3] bg-[#F0EBE1] mb-4 overflow-hidden relative">
                <AuthedImage key={`${p.id}-${p.rotated_at || ''}`} photoId={p.id} className="w-full h-full object-cover"/>
                <div className="absolute bottom-2 right-2 flex gap-1">
                  <button
                    onClick={() => rotate(p.id, "ccw")}
                    className="bg-white/90 hover:bg-white p-1.5 shadow"
                    title="Rotate 90° left"
                    data-testid={`pending-rotate-ccw-${p.id}`}
                  >
                    <RotateCcw className="w-4 h-4"/>
                  </button>
                  <button
                    onClick={() => rotate(p.id, "cw")}
                    className="bg-white/90 hover:bg-white p-1.5 shadow"
                    title="Rotate 90° right"
                    data-testid={`pending-rotate-cw-${p.id}`}
                  >
                    <RotateCw className="w-4 h-4"/>
                  </button>
                </div>
              </div>
              <div className="space-y-2 mb-4 text-sm">
                <div className="flex items-center gap-2 text-[#5C5C5C]">
                  <UserCircle2 className="w-4 h-4"/> Uploaded by <strong>{p.uploader_screen_name}</strong>
                  {p.uploader_email && <span className="text-xs text-[#8A8A8A]" data-testid={`pending-submitter-${p.id}`}>· {p.uploader_email}</span>}
                </div>
                {p.date_taken && <div className="flex items-center gap-2 text-[#5C5C5C]"><Calendar className="w-4 h-4"/>{p.date_taken}</div>}
                {p.location && <div className="flex items-center gap-2 text-[#5C5C5C]"><MapPin className="w-4 h-4"/>{p.location}</div>}
                {p.caption && <p className="text-[#1F1F1F] italic">"{p.caption}"</p>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => decide(p, "private")} className="btn-secondary text-sm" data-testid={`btn-keep-private-${p.id}`}>
                  <Lock className="w-3.5 h-3.5"/> Keep private
                </button>
                <select
                  data-testid={`select-album-${p.id}`}
                  onChange={(e) => e.target.value && decide(p, "public", e.target.value)}
                  defaultValue=""
                  className="mp-input text-sm w-auto"
                >
                  <option value="">Move to public album...</option>
                  {albums.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
                </select>
                <button onClick={() => decide(p, "delete")} className="btn-ghost text-[#8C3A3A] text-sm" data-testid={`btn-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4"/> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
